// Compras / e-Facturas recibidas (Limpack como receptor)
//
// Soporta carga manual + parseo automático de XML CFE (DGI Uruguay)
// Esquema CFE: https://www.dgi.gub.uy/wdgi/page?2,principal,_Ampliacion,O,es,0,PAG;CONC;1099;7;D;modelo-de-datos-cfe;1;PAG;

import { supabase } from './supabase'

// ============================================================
// PARSER XML CFE DGI
// ============================================================

const TIPO_CFE_LABEL = {
  101: 'e-Ticket',
  102: 'Nota de Crédito de e-Ticket',
  103: 'Nota de Débito de e-Ticket',
  111: 'e-Factura',
  112: 'Nota de Crédito de e-Factura',
  113: 'Nota de Débito de e-Factura',
  121: 'e-Factura Exportación',
  122: 'Nota de Crédito de e-Factura Exportación',
  123: 'Nota de Débito de e-Factura Exportación',
  131: 'e-Remito',
  141: 'e-Resguardo',
  151: 'e-Factura Contingencia',
  201: 'e-Ticket Venta por Cuenta Ajena',
  211: 'e-Factura Venta por Cuenta Ajena',
}

const FORMA_PAGO_CFE = {
  1: 'Contado',
  2: 'Crédito',
}

/**
 * Parsea un XML CFE de DGI y devuelve los datos estructurados.
 * @param {string} xmlString - Contenido del XML
 * @returns {object} Datos extraídos { emisor, comprobante, importes, items, formaPago }
 */
export function parseXmlCfe(xmlString) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'application/xml')

  // Verificar errores de parsing
  const parseError = doc.querySelector('parsererror')
  if (parseError) throw new Error('XML inválido: ' + parseError.textContent.slice(0, 200))

  // Helper: get text de un nodo (con namespaces variables)
  const get = (selector) => {
    // Probar variantes con/sin namespace
    const el = doc.querySelector(selector) ||
               doc.querySelector(selector.replace(/^[a-z]+:/i, '')) ||
               findByLocalName(doc, selector)
    return el?.textContent?.trim() || null
  }

  const getNum = (selector) => {
    const v = get(selector)
    return v ? parseFloat(v) : 0
  }

  const getInt = (selector) => {
    const v = get(selector)
    return v ? parseInt(v, 10) : null
  }

  // Encabezado
  const tipoCfe = getInt('TipoCFE') || getInt('IdDoc TipoCFE')
  const serie = get('Serie') || get('IdDoc Serie')
  const numero = getInt('Nro') || getInt('IdDoc Nro')
  const fchEmis = get('FchEmis') || get('IdDoc FchEmis')

  // Emisor
  const emisorRut = get('Emisor RUCEmisor') || get('RUCEmisor')
  const emisorRazon = get('Emisor RznSoc') || get('RznSoc')

  // Receptor
  const receptorRut = get('Receptor RUCRecep') || get('RUCRecep')
  const receptorRazon = get('Receptor RznSocRecep') || get('RznSocRecep')

  // CAE
  const cae = get('CAEData CAE_ID') || get('CAE_ID') || get('CAEData CAEID')

  // Totales
  const montoNeto = getNum('MntNetoIvaTasaBasica') + getNum('MntNetoIVATasaMin')
  const ivaBasica = getNum('IVATasaBasica') // 22%
  const ivaMinima = getNum('IVATasaMin')    // 10%
  const exento = getNum('MntNoGrv')
  const total = getNum('MntTotal')
  const moneda = get('TpoMoneda') || 'UYU'

  // Forma de pago
  const fmaPagoCode = getInt('FmaPago')
  const formaPago = fmaPagoCode ? (FORMA_PAGO_CFE[fmaPagoCode] || 'Contado') : null

  // Items (Detalle)
  const items = []
  const detalleNodes = doc.querySelectorAll('Item') || findAllByLocalName(doc, 'Item')
  detalleNodes.forEach((node, idx) => {
    items.push({
      orden: idx + 1,
      descripcion: getChildText(node, 'NomItem') || getChildText(node, 'DscItem') || '',
      cantidad: parseFloat(getChildText(node, 'Cantidad') || '0') || null,
      precio_unitario: parseFloat(getChildText(node, 'PrecioUnitario') || '0') || null,
      monto_item: parseFloat(getChildText(node, 'MontoItem') || '0') || null,
      unidad: getChildText(node, 'UniMed') || 'u',
    })
  })

  return {
    valido: !!(tipoCfe && emisorRut && fchEmis),
    emisor: {
      rut: emisorRut,
      razonSocial: emisorRazon,
    },
    receptor: {
      rut: receptorRut,
      razonSocial: receptorRazon,
    },
    comprobante: {
      tipoCfe,
      tipoCfeLabel: TIPO_CFE_LABEL[tipoCfe] || `Tipo ${tipoCfe}`,
      serie,
      numero,
      fechaEmision: fchEmis,
      cae,
    },
    importes: {
      neto: montoNeto || (total - ivaBasica - ivaMinima - exento),
      ivaBasica,
      ivaMinima,
      exento,
      total,
      moneda,
    },
    formaPago,
    items,
  }
}

// Helpers para namespaces variables
function findByLocalName(doc, name) {
  const localName = name.split(' ').pop()
  return Array.from(doc.getElementsByTagName('*')).find(el => el.localName === localName)
}

function findAllByLocalName(doc, name) {
  return Array.from(doc.getElementsByTagName('*')).filter(el => el.localName === name)
}

function getChildText(parentNode, localName) {
  const child = Array.from(parentNode.children).find(c => c.localName === localName || c.tagName === localName)
  return child?.textContent?.trim() || null
}

// ============================================================
// API: Auto-detectar proveedor por RUT
// ============================================================

export async function getProveedorPorRut(rut) {
  if (!rut) return null
  const { data } = await supabase
    .from('proveedores')
    .select('id, nombre, rut')
    .eq('rut', rut)
    .maybeSingle()
  return data
}

// ============================================================
// API: Sugerir cruce de items con catálogo productos
// ============================================================

export async function sugerirProductos(items) {
  if (!items || items.length === 0) return []

  // Cargar catálogo productos del proveedor (o todos)
  const { data: productos } = await supabase
    .from('productos')
    .select('id, sku, nombre, marca, presentacion')
    .eq('activo', true)

  if (!productos) return items

  // Matching simple: similitud de nombre + presentación
  return items.map(item => {
    const desc = (item.descripcion || '').toLowerCase()
    const match = productos.find(p => {
      const nombreP = (p.nombre || '').toLowerCase()
      const marcaP = (p.marca || '').toLowerCase()
      const presP = (p.presentacion || '').toLowerCase()
      return desc.includes(nombreP.slice(0, 8)) ||
             (marcaP && desc.includes(marcaP)) ||
             (presP && desc.includes(presP))
    })
    return { ...item, producto_sugerido: match || null }
  })
}

// ============================================================
// API: Crear compra a partir de XML parseado
// ============================================================

export async function crearCompraDesdeXml(parsedData, opciones = {}) {
  const { emisor, comprobante, importes, formaPago, items } = parsedData

  // Buscar proveedor automáticamente por RUT
  const proveedor = await getProveedorPorRut(emisor.rut)

  // Subir XML a storage
  let xmlUrl = null
  if (opciones.xmlFile) {
    const fileName = `${emisor.rut}_${comprobante.serie}_${comprobante.numero}_${Date.now()}.xml`
    const { data: upload, error: uploadErr } = await supabase.storage
      .from('facturas-compras')
      .upload(fileName, opciones.xmlFile)
    if (!uploadErr && upload) xmlUrl = upload.path
  }

  // Subir PDF si existe
  let pdfUrl = null
  if (opciones.pdfFile) {
    const fileName = `${emisor.rut}_${comprobante.serie}_${comprobante.numero}_${Date.now()}.pdf`
    const { data: upload, error: uploadErr } = await supabase.storage
      .from('facturas-compras')
      .upload(fileName, opciones.pdfFile)
    if (!uploadErr && upload) pdfUrl = upload.path
  }

  // Insertar compra
  const { data: compra, error } = await supabase
    .from('compras')
    .insert({
      proveedor_id: proveedor?.id || null,
      emisor_rut: emisor.rut,
      emisor_razon_social: emisor.razonSocial,
      tipo_cfe: comprobante.tipoCfe,
      serie: comprobante.serie,
      numero: comprobante.numero,
      fecha_emision: comprobante.fechaEmision,
      cae: comprobante.cae,
      monto_neto: importes.neto,
      monto_iva_basica: importes.ivaBasica,
      monto_iva_minima: importes.ivaMinima,
      monto_exento: importes.exento,
      monto_total: importes.total,
      moneda: importes.moneda,
      forma_pago: opciones.formaPago || formaPago,
      cuenta_contable: opciones.cuentaContable || null,
      centro_costo: opciones.centroCosto || null,
      xml_url: xmlUrl,
      pdf_url: pdfUrl,
      cargado_via: 'xml-upload',
      notas: opciones.notas || null,
    })
    .select()
    .single()

  if (error) throw error

  // Insertar items
  if (items && items.length > 0) {
    const itemsToInsert = items.map(it => ({
      compra_id: compra.id,
      descripcion: it.descripcion,
      cantidad: it.cantidad,
      precio_unitario: it.precio_unitario,
      monto_item: it.monto_item,
      unidad: it.unidad,
      orden: it.orden,
      producto_id: it.producto_id || null,
    }))
    await supabase.from('compras_items').insert(itemsToInsert)
  }

  return compra
}

// ============================================================
// API: Listar compras
// ============================================================

export async function listarCompras(filtros = {}) {
  let q = supabase
    .from('compras')
    .select(`
      id, fecha_emision, tipo_cfe, serie, numero, cae,
      emisor_rut, emisor_razon_social,
      monto_neto, monto_iva_basica, monto_iva_minima, monto_exento, monto_total, moneda,
      forma_pago, fecha_pago, estado,
      cuenta_contable, centro_costo,
      xml_url, pdf_url,
      proveedor:proveedores(id, nombre),
      cargado_via, asiento_id, created_at
    `)
    .order('fecha_emision', { ascending: false })

  if (filtros.proveedor_id) q = q.eq('proveedor_id', filtros.proveedor_id)
  if (filtros.estado) q = q.eq('estado', filtros.estado)
  if (filtros.fecha_desde) q = q.gte('fecha_emision', filtros.fecha_desde)
  if (filtros.fecha_hasta) q = q.lte('fecha_emision', filtros.fecha_hasta)

  const { data, error } = await q
  if (error) throw error
  return data || []
}

// ============================================================
// API: Crear compra MANUAL (sin XML)
// ============================================================

export async function crearCompraManual(datos) {
  const { data, error } = await supabase
    .from('compras')
    .insert({
      proveedor_id: datos.proveedor_id || null,
      emisor_rut: datos.emisor_rut,
      emisor_razon_social: datos.emisor_razon_social,
      tipo_cfe: datos.tipo_cfe || 111,
      serie: datos.serie,
      numero: datos.numero,
      fecha_emision: datos.fecha_emision,
      monto_neto: datos.monto_neto || 0,
      monto_iva_basica: datos.monto_iva_basica || 0,
      monto_iva_minima: datos.monto_iva_minima || 0,
      monto_exento: datos.monto_exento || 0,
      monto_total: datos.monto_total,
      forma_pago: datos.forma_pago,
      cuenta_contable: datos.cuenta_contable || null,
      centro_costo: datos.centro_costo || null,
      cargado_via: 'manual',
      notas: datos.notas || null,
    })
    .select()
    .single()
  if (error) throw error
  return data
}

// ============================================================
// API: Marcar como pagada
// ============================================================

export async function marcarComoPagada(compraId, fechaPago, formaPago) {
  const { error } = await supabase
    .from('compras')
    .update({
      estado: 'pagada',
      fecha_pago: fechaPago,
      forma_pago: formaPago,
    })
    .eq('id', compraId)
  if (error) throw error
}

// ============================================================
// API: Eliminar compra (solo si pendiente)
// ============================================================

export async function eliminarCompra(compraId) {
  const { error } = await supabase
    .from('compras')
    .delete()
    .eq('id', compraId)
  if (error) throw error
}

// ============================================================
// API: Export CSV para Memory
// ============================================================

export function compraACsv(compras) {
  const headers = [
    'Fecha', 'Proveedor', 'RUT', 'Tipo_CFE', 'Serie', 'Numero',
    'Concepto', 'Neto', 'IVA_22', 'IVA_10', 'Exento', 'Total',
    'Moneda', 'Forma_Pago', 'Estado', 'Cuenta', 'Centro_Costo'
  ]
  const rows = compras.map(c => [
    c.fecha_emision,
    c.proveedor?.nombre || c.emisor_razon_social || '',
    c.emisor_rut,
    TIPO_CFE_LABEL[c.tipo_cfe] || c.tipo_cfe,
    c.serie,
    c.numero,
    c.proveedor?.nombre || c.emisor_razon_social || '',
    Number(c.monto_neto).toFixed(2),
    Number(c.monto_iva_basica).toFixed(2),
    Number(c.monto_iva_minima).toFixed(2),
    Number(c.monto_exento).toFixed(2),
    Number(c.monto_total).toFixed(2),
    c.moneda || 'UYU',
    c.forma_pago || '',
    c.estado || 'pendiente',
    c.cuenta_contable || '',
    c.centro_costo || '',
  ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
  return [headers.join(','), ...rows].join('\n')
}

export function descargarCsv(filename, csvContent) {
  const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}
